import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, Star, AlertTriangle, Info } from "lucide-react";
import { stateVAExemptions } from "@/types/vaExemptions";

interface StateVABenefitsInfoProps {
  state: string;
}

export const StateVABenefitsInfo: React.FC<StateVABenefitsInfoProps> = ({ state }) => {
  const stateInfo = stateVAExemptions[state];
  
  if (!stateInfo) {
    return (
      <Alert className="bg-muted/50 border-muted">
        <Info className="w-4 h-4" />
        <AlertDescription className="text-sm">
          VA property tax exemption details not yet configured for {state}. 
          Contact your county assessor for information about local veteran benefits.
        </AlertDescription>
      </Alert>
    );
  }
  
  return (
    <Card className="border-primary/30 bg-card">
      <CardHeader>
        <CardTitle className="text-lg">{stateInfo.state} VA Property Tax Benefits</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h4 className="font-semibold mb-2 text-sm">Rating Tiers:</h4>
          <ul className="space-y-2">
            {stateInfo.ratingTiers.map((tier, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span>
                  <strong>{tier.minRating}%-{tier.maxRating}%:</strong> {tier.description}
                </span>
              </li>
            ))}
          </ul>
        </div>
        
        {Object.keys(stateInfo.specialProvisions).length > 0 && (
          <div>
            <h4 className="font-semibold mb-2 text-sm">Special Provisions:</h4>
            <ul className="space-y-2">
              {stateInfo.specialProvisions.age65Plus && (
                <li className="flex items-start gap-2 text-sm">
                  <Star className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>{stateInfo.specialProvisions.age65Plus.description}</span>
                </li>
              )}
              {stateInfo.specialProvisions.fullExemption && (
                <li className="flex items-start gap-2 text-sm">
                  <Star className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>{stateInfo.specialProvisions.fullExemption.description}</span>
                </li>
              )}
            </ul>
          </div>
        )}
        
        <Alert className="bg-muted/50 border-muted">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription className="text-xs">
            <strong>Important:</strong> You must apply for these exemptions with your county assessor's office.
            This calculator estimates benefits based on state law, but local implementation may vary.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};
